import { useGetOrgChartDataQuery } from '@/features/employee-data/employee-data.api.ts'
import { RawNodeDatum } from 'react-d3-tree'
import { useCallback, useEffect, useState } from 'react'
import constate from 'constate'
import dayjs from 'dayjs'
import { EmployeeOrgChartNodeInfo } from '@/features/employee-data/organization-chart.def.ts'
import { groupBy, sum, uniq } from 'lodash-es'
import { EmploymentType } from '@/common/types/employment.def.ts'
import { SubDepartment } from '@/common/types/work-info.def.ts'

export interface HcTreeNodeDatum extends RawNodeDatum {
  extra?: {
    listChildren?: RawNodeDatum[]
  }
  children?: HcTreeNodeDatum[]
}

export const orgChartTypes = ['Default', 'Investment Only', 'By Department', 'By Location'] as const
export type OrgChartType = typeof orgChartTypes[number]
export type OrgChartFilterType =
  'countries'
  | 'locations'
  | 'departments'
  | 'subDepartments'
  | 'employmentTypes'
  | 'fields'
export type FilterOption = {
  label: string
  value: string
}

export const orgChartFieldOptions = [
  { label: 'Title', value: 'title' },
  { label: 'Department', value: 'department' },
  { label: 'Location', value: 'location' },
  { label: 'Employment Type', value: 'employmentType' },
  { label: 'Total Under', value: 'totalUnder' },
] as FilterOption[]

export type OrgChartFilter = Record<OrgChartFilterType, string[]>
export type OrgChartFilterOptions = Record<OrgChartFilterType, FilterOption[]>

export const [OrgChartInfoProvider, useOrgChartInfo] = constate(() => {
  const [effectiveDate, setEffectiveDate] = useState<string>(dayjs().format('YYYY-MM-DD'))
  const { data, isFetching, refetch, error } = useGetOrgChartDataQuery({ effectiveDate })
  const [orgChartType, setOrgChartType] = useState<OrgChartType>(orgChartTypes[0])
  const [orgChartData, setOrgChartData] = useState<HcTreeNodeDatum>()
  // for department type org chart only
  const [selectedDepartment, setSelectedDepartment] = useState<string>()
  const [selectedLocation, setSelectedLocation] = useState<string>()
  const [filters, setFilters] = useState<OrgChartFilter>({
    countries: [],
    locations: [],
    departments: [],
    subDepartments: [],
    employmentTypes: [],
    fields: [],
  })
  const [filterOptions, setFilterOptions] = useState<OrgChartFilterOptions>({
    countries: [],
    locations: [],
    departments: [],
    subDepartments: [],
    employmentTypes: [],
    fields: [],
  })

  const resetFilter = useCallback(() => {
    if (data && data.length > 0) {
      const allDepartments = uniq(data.map(x => x.department))
      const allSubDepartments = uniq(data.map(x => x.subDepartment))
      const allLocations = uniq(data.map(x => x.officeLocation.city))
      const allEmploymentTypes = uniq(data.map(x => x.employmentType))
      const allCountries = uniq(data.map(x => x.country))

      setFilters({
        countries: allCountries,
        locations: allLocations,
        subDepartments: allSubDepartments,
        departments: allDepartments,
        employmentTypes: allEmploymentTypes,
        fields: orgChartFieldOptions.map(x => x.value),
      })
      setFilterOptions({
        countries: allCountries.map(x => ({ label: x, value: x })),
        locations: allLocations.map(x => ({ label: x, value: x })),
        departments: allDepartments.map(x => ({ label: x, value: x })),
        subDepartments: allSubDepartments.map(x => ({ label: x, value: x })),
        employmentTypes: allEmploymentTypes.map(x => ({ label: x, value: x })),
        fields: orgChartFieldOptions,
      })
    } else {
      setFilterOptions({
        countries: [],
        locations: [],
        departments: [],
        employmentTypes: [],
        fields: [],
        subDepartments: [],
      })
    }
  }, [data])

  useEffect(() => {
    resetFilter()
    setSelectedDepartment(undefined)
    setSelectedLocation(undefined)
  }, [data, resetFilter, orgChartType])

  useEffect(() => {
    if (data) {
      const { root } = buildOrgTreeData(data, orgChartType, filters, selectedDepartment, selectedLocation)
      setOrgChartData(root)
    }
  }, [orgChartType, filters, data, selectedDepartment, selectedLocation])

  return {
    orgChartType,
    setOrgChartType,
    selectedDepartment,
    setSelectedDepartment,
    selectedLocation,
    setSelectedLocation,
    orgChartData,
    orgChartDataError: error,
    showTitle: filters.fields.includes('title'),
    showDepartment: filters.fields.includes('department'),
    showLocation: filters.fields.includes('location'),
    showEmploymentType: filters.fields.includes('employmentType'),
    showTotalUnder: filters.fields.includes('totalUnder'),
    filters,
    setFilters,
    filterOptions,
    isLoading: isFetching,
    refetch,
    effectiveDate,
    setEffectiveDate,
  }
})

type orgTreeDataResponse = {
  root: HcTreeNodeDatum,
}

const createHcTreeNodeDatum = (nodeInfo: EmployeeOrgChartNodeInfo, show: boolean): HcTreeNodeDatum => {
  return {
    name: nodeInfo.name,
    attributes: {
      employeeId: nodeInfo.employeeId,
      reportingManager: nodeInfo.reportingManager,
      name: nodeInfo.name,
      title: nodeInfo.title,
      department: nodeInfo.department,
      subDepartment: nodeInfo.subDepartment,
      employmentType: nodeInfo.employmentType,
      country: nodeInfo.country,
      city: nodeInfo.officeLocation.city,
      officeCode: nodeInfo.officeLocation.code || '',
      show,
    },
    children: [],
  }
}

const assignTotalNumUnder = (node: HcTreeNodeDatum) => {
  const attributes = node.attributes as unknown as EmployeeOrgChartNodeInfo

  if (attributes.totalCountUnder !== undefined) return

  let totalCount = 0

  if (node.extra?.listChildren?.length) {
    totalCount += (node.extra?.listChildren || []).filter(x => x.attributes?.show).length
  }

  if (node.children?.length) {
    node.children.forEach(x => assignTotalNumUnder(x))
    totalCount += node.children.filter(x => {
      if (x.extra?.listChildren?.length) return false
      if (x.attributes?.show) return true
    }).length + sum(node.children.map(x => x.attributes?.totalCountUnder))
  }

  (node.attributes as unknown as EmployeeOrgChartNodeInfo).totalCountUnder = totalCount
  return
}

const buildDefaultOrgChart = (parentNode: HcTreeNodeDatum, employee: EmployeeOrgChartNodeInfo, allEmployees: EmployeeOrgChartNodeInfo[], filters: OrgChartFilter) => {
  const show =
    filters.departments.includes(employee.department) &&
    filters.subDepartments.includes(employee.subDepartment) &&
    filters.locations.includes(employee.officeLocation.city) &&
    filters.employmentTypes.includes(employee.employmentType)

  const orgNode = createHcTreeNodeDatum(employee, show)

  parentNode.children ??= []
  parentNode.children.push(orgNode)

  const directReports = allEmployees.filter(e => e.reportingManager === employee.employeeId)
  directReports.forEach(directReport => buildDefaultOrgChart(orgNode, directReport, allEmployees, filters))
}

const buildByLocationOrgChart = (polymerNode: HcTreeNodeDatum, allEmployees: EmployeeOrgChartNodeInfo[], filters: OrgChartFilter, selectedLocation?: string) => {
  const employees = allEmployees.filter(x =>
    x.country === selectedLocation &&
    filters.departments.includes(x.department) &&
    filters.subDepartments.includes(x.subDepartment) &&
    filters.employmentTypes.includes(x.employmentType))

  const departmentGroups = groupBy(employees, x => x.department)
  Object.keys(departmentGroups).forEach(department => {
    polymerNode.children?.push({
      name: department,
      attributes: {
        name: department,
        employmentType: EmploymentType.Permanent,
        show: true,
      },
      extra: {
        listChildren: departmentGroups[department].map(x => createHcTreeNodeDatum(x, true)),
      },
    })
  })
}

const buildInvestmentOnlyOrgChart = (polymerNode: HcTreeNodeDatum, allEmployees: EmployeeOrgChartNodeInfo[], filters: OrgChartFilter) => {
  const investmentEmployees = allEmployees.filter(x => x.subDepartment === SubDepartment.Investment)

  const existSet = new Set(investmentEmployees.map(x => x.employeeId))
  const finalList = investmentEmployees.map(x => {
    return {
      ...x,
      reportingManager: existSet.has(x.reportingManager) ? x.reportingManager : undefined,
    } as EmployeeOrgChartNodeInfo
  })

  finalList.filter(e => !e.reportingManager).forEach(e => buildDefaultOrgChart(polymerNode, e, finalList, filters))
}

const buildByDepartmentOrgChart = (polymerNode: HcTreeNodeDatum, allEmployees: EmployeeOrgChartNodeInfo[], filters: OrgChartFilter, selectedDepartment?: string) => {
  const departmentEmployees = allEmployees.filter(x => x.department === selectedDepartment)

  const existSet = new Set(departmentEmployees.map(x => x.employeeId))
  const finalList = departmentEmployees.map(x => {
    return {
      ...x,
      reportingManager: existSet.has(x.reportingManager) ? x.reportingManager : undefined,
    } as EmployeeOrgChartNodeInfo
  })

  finalList.filter(e => !e.reportingManager).forEach(e => buildDefaultOrgChart(polymerNode, e, finalList, filters))
}

const buildOrgTreeData = (data: EmployeeOrgChartNodeInfo[], type: OrgChartType, filters: OrgChartFilter, selectedDepartment?: string, selectedLocation?: string): orgTreeDataResponse => {
  const polymer: HcTreeNodeDatum = {
    name: 'Polymer',
    attributes: {
      name: 'Polymer',
      employmentType: EmploymentType.Permanent,
      show: true,
    },
    children: [],
  }

  switch (type) {
    case 'Investment Only':
      buildInvestmentOnlyOrgChart(polymer, data, filters)
      break
    case 'By Department':
      buildByDepartmentOrgChart(polymer, data, filters, selectedDepartment)
      if (selectedDepartment) {
        polymer.name = selectedDepartment
        if (polymer.attributes) polymer.attributes.name = selectedDepartment
      }
      break
    case 'By Location':
      buildByLocationOrgChart(polymer, data, filters, selectedLocation)
      if (selectedLocation) {
        polymer.name = selectedLocation
        if (polymer.attributes) polymer.attributes.name = selectedLocation
      }
      break
    default:
      data.filter(e => !e.reportingManager).forEach(e => buildDefaultOrgChart(polymer, e, data, filters))
      break
  }

  assignTotalNumUnder(polymer)

  return {
    root: polymer,
  }
}
